import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { toast } from 'sonner'

export interface AdImageBatchItemInput {
  ad_image_id: number
  order: number
  is_active?: boolean
}

export interface CreateAdImageBatchInput {
  batch_name: string
  description?: string
  overlay_interval_ms: number
  provider_id?: number
  items?: AdImageBatchItemInput[]
}

export interface UpdateAdImageBatchInput {
  batch_name?: string
  description?: string
  overlay_interval_ms?: number
  is_active?: boolean
}

export interface ApplyAdImageBatchInput {
  clinic_ids?: number[]
  broadcast_device_ids?: number[]
}

// 後端回傳的單筆 / 多筆批次資料形狀（前端僅讀取 success 與 data 是否存在）
interface AdImageBatchResponse {
  success: boolean
  data?: Record<string, unknown>
  error?: { code: string; message: string }
}

interface AdImageBatchListResponse {
  success: boolean
  data?: Record<string, unknown>[]
  error?: { code: string; message: string }
}

export const adImageBatchService = {
  // 獲取所有批次
  getAllBatches: async (
    params: { includeInactive?: boolean; providerId?: number } = {},
    token?: string,
  ) => {
    try {
      const queryParams: Record<string, unknown> = {}
      if (params.includeInactive) queryParams.includeInactive = true
      if (params.providerId) queryParams.providerId = params.providerId

      const response = await apiClient.get<AdImageBatchListResponse>('/api/ad-image-batches', {
        params: queryParams,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('獲取廣告圖片批次列表失敗')
        return error.response.data
      }
      toast.error('獲取廣告圖片批次列表失敗')
      return { success: false, data: [] }
    }
  },

  // 獲取單個批次
  getBatchById: async (batch_id: number, token: string) => {
    try {
      const response = await apiClient.get<AdImageBatchResponse>(
        `/api/ad-image-batches/${batch_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('獲取廣告圖片批次失敗')
        return error.response.data
      }
      toast.error('獲取廣告圖片批次失敗')
      return { success: false }
    }
  },

  // 創建批次
  createBatch: async (data: CreateAdImageBatchInput, token: string) => {
    try {
      const response = await apiClient.post<AdImageBatchResponse>('/api/ad-image-batches', data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.success) {
        toast.success('廣告圖片批次已創建')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '創建失敗')
        return error.response.data
      }
      toast.error('創建失敗')
      return { success: false }
    }
  },

  // 更新批次基本資訊
  updateBatch: async (batch_id: number, data: UpdateAdImageBatchInput, token: string) => {
    try {
      const response = await apiClient.put<AdImageBatchResponse>(
        `/api/ad-image-batches/${batch_id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('批次已更新')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '更新失敗')
        return error.response.data
      }
      toast.error('更新失敗')
      return { success: false }
    }
  },

  // 全量替換批次項目（新增/移除/調整順序）
  replaceItems: async (batch_id: number, items: AdImageBatchItemInput[], token: string) => {
    try {
      const response = await apiClient.put<AdImageBatchResponse>(
        `/api/ad-image-batches/${batch_id}/items`,
        { items },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('圖片項目已更新')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '更新圖片項目失敗')
        return error.response.data
      }
      toast.error('更新圖片項目失敗')
      return { success: false }
    }
  },

  // 套用批次
  applyBatch: async (batch_id: number, data: ApplyAdImageBatchInput, token: string) => {
    try {
      const response = await apiClient.post<AdImageBatchListResponse>(
        `/api/ad-image-batches/${batch_id}/apply`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('批次已套用')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '套用失敗')
        return error.response.data
      }
      toast.error('套用失敗')
      return { success: false }
    }
  },

  // 獲取批次套用記錄
  getBatchApplications: async (batch_id: number, token: string) => {
    try {
      const response = await apiClient.get<AdImageBatchResponse>(
        `/api/ad-image-batches/${batch_id}/applications`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('獲取套用記錄失敗')
        return error.response.data
      }
      toast.error('獲取套用記錄失敗')
      return { success: false }
    }
  },

  // 移除套用
  removeApplication: async (application_id: number, token: string) => {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/api/ad-image-batches/applications/${application_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('已移除套用')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('移除失敗')
        return error.response.data
      }
      toast.error('移除失敗')
      return { success: false }
    }
  },

  // 刪除批次
  deleteBatch: async (batch_id: number, token: string) => {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/api/ad-image-batches/${batch_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('批次已刪除')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('刪除失敗')
        return error.response.data
      }
      toast.error('刪除失敗')
      return { success: false }
    }
  },
}
